import { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Checkbox, Input } from '@vd/ui';
import { Search } from 'lucide-react';
import type { User } from '@vd/types';
import { useAuth } from '../auth/AuthProvider';
import styles from './AssigneePicker.module.css';

export type AssignMode = 'add' | 'replace' | 'remove';

export interface AssigneePickerProps {
  allMembers: User[];
  selected: string[];
  /**
   * `mode` is meaningful only in `bulkMode` — single-ticket callers
   * receive 'replace' and can ignore it.
   */
  onDone: (userIds: string[], mode: AssignMode) => void;
  onCancel: () => void;
  bulkMode?: boolean;
}

export function AssigneePicker({ allMembers, selected, onDone, onCancel, bulkMode }: AssigneePickerProps) {
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [current, setCurrent] = useState<Set<string>>(() => new Set(selected));
  // In bulk mode the user picks a mode that decides how the selected
  // userIds are applied across every checked ticket:
  //   - replace: clear existing assignees, set to picked
  //   - add:     append picked to existing (no-op if already there)
  //   - remove:  delete picked from existing
  // Default to replace so the simple "assign these people" mental
  // model needs no extra clicks. Single-ticket callers always behave
  // as replace (the picker shows current assignees and the user toggles
  // them on/off); the mode argument is ignored on their side.
  const [mode, setMode] = useState<AssignMode>('replace');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const filter = q.trim().toLowerCase();
  const filtered = allMembers.filter((u) =>
    !filter || u.name.toLowerCase().includes(filter) || u.email.toLowerCase().includes(filter),
  );
  const sorted = [...filtered].sort((a, b) => {
    const aSel = current.has(a.id) ? 0 : 1;
    const bSel = current.has(b.id) ? 0 : 1;
    if (aSel !== bSel) return aSel - bSel;
    return a.name.localeCompare(b.name);
  });

  const toggle = (id: string) => {
    setCurrent((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const assignMe = user && !current.has(user.id);

  return (
    <div className={styles.picker}>
      {bulkMode && (
        <div className={styles.bulkBar}>
          <label><input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} /> Replace</label>
          <label><input type="radio" checked={mode === 'add'} onChange={() => setMode('add')} /> Add</label>
          <label><input type="radio" checked={mode === 'remove'} onChange={() => setMode('remove')} /> Remove</label>
        </div>
      )}
      <div className={styles.searchWrap}>
        <Search size={14} style={{ color: 'var(--text-tertiary)' }} />
        <Input ref={inputRef} className={styles.searchInput} placeholder="Search members…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className={styles.list}>
        {assignMe && user && (
          <button
            className={styles.row}
            // "Assign me" is a direct action, not a toggle — one click
            // commits and closes the picker. Previously this just ticked
            // a checkbox and users had to also click "Done", which felt
            // broken ("nothing happens"). For the single-ticket case we
            // replace the assignee set with +me; for bulk we add +me to
            // whatever was already selected.
            onClick={() => {
              const next = new Set(current);
              next.add(user.id);
              // Single-ticket: replace assignees with chosen set + me.
              // Bulk: respect the chosen mode for consistency with
              // what the radio buttons say (most user-friendly when
              // they're staring at "Add" + click "Assign me").
              onDone(Array.from(next), bulkMode ? mode : 'replace');
            }}
            style={{ fontWeight: 500 }}
          >
            <Avatar name={user.name} size="sm" />
            <div className={styles.info}>
              <div className={styles.name}>Assign me</div>
            </div>
            <Checkbox checked={current.has(user.id)} readOnly />
          </button>
        )}
        {sorted.map((u) => (
          <button key={u.id} className={styles.row} onClick={() => toggle(u.id)}>
            <Avatar name={u.name} size="sm" />
            <div className={styles.info}>
              <div className={styles.name}>{u.name}</div>
              <div className={styles.email}>{u.email}</div>
            </div>
            <Checkbox checked={current.has(u.id)} readOnly />
          </button>
        ))}
        {sorted.length === 0 && <div className={styles.empty}>No members match.</div>}
      </div>
      <div className={styles.footer}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onDone(Array.from(current), bulkMode ? mode : 'replace')}
          disabled={bulkMode && mode !== 'replace' && current.size === 0}
        >
          Done{bulkMode ? ` (${mode})` : ''}
        </Button>
      </div>
    </div>
  );
}
